<!--
 * @Description: 
 * @Date: 2022-09-19 11:03:48
-->
<template>
  <div ref="TableDomRef">
    <BaseTable
      ref="TableRef"
      :data="_data"
      :columns="tableConfig.tableColumns"
      v-bind="tableConfig.baseConfig"
    ></BaseTable>
  </div>
</template>

<script lang="tsx" setup>
import {
  XrkTableProps,
  XrkTableRef,
  BaseTable,
  BaseIcon,
  BaseButton,
  BaseToolTip
} from 'xrk-components';
import { dealTableColumnstextLineClamp } from '../../tools/index';
import { Product } from '../../data-source/types';
import {
  defineExpose,
  ref,
  nextTick,
  watch,
  defineEmits,
  defineProps
} from 'vue';
import Sortable from 'sortablejs';
import { Sort } from '@element-plus/icons-vue';
import { ProductTableColumns } from './index';
import { cloneDeep } from 'lodash';

type ColumnsType = Product['row'];
type TableConfig = XrkTableProps<{}, ColumnsType>;

const props = defineProps<{
  disabled: boolean;
}>();
const emits = defineEmits<{
  (event: 'change', data: Array<ColumnsType>): void;
}>();

const DragButtonClassName = 'xrk-table--drag__btn';
const DragDisabledClassName = 'xrk-table--drag__disabled';
const { TableRef, TableDomRef } = (() => {
  const TableDomRef = ref<HTMLDivElement>();
  const TableRef = ref<XrkTableRef>();
  return { TableRef, TableDomRef };
})();

const _data = ref<Array<ColumnsType>>([]);
const _dataIdSort = ref<Array<number>>([]);
const setDataSort = () => {
  _dataIdSort.value = [];
  _data.value.forEach((row, index) => {
    _dataIdSort.value[index] = row.id;
  });
};

const getSortData = (
  sortData: (typeof _dataIdSort)['value'],
  data: (typeof _data)['value']
) => {
  const map = data.reduce((map, row) => {
    map[row.id] = row;
    return map;
  }, {} as any);
  return sortData.map((id, index) => ({
    ...map[id],
    sort: index + 1
  }));
};

const tableConfig: TableConfig['tableConfig'] = {
  baseConfig: {
    height: 400,
    rowKey: 'productId'
  },
  tableColumns: dealTableColumnstextLineClamp<
    TableConfig['tableConfig']['tableColumns']
  >([
    ...ProductTableColumns,
    {
      width: 80,
      label: '排序',
      formatter: ({ row }) => _dataIdSort.value.indexOf(row.id) + 1
    },
    {
      width: 100,
      label: '操作',
      render: ({ row }) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 24,
              justifyContent: 'center'
            }}
          >
            <BaseButton
              link
              type="primary2"
              disabled={props.disabled}
              click={() => {
                _dataIdSort.value.splice(_dataIdSort.value.indexOf(row.id), 1);
                _data.value = getSortData(
                  _dataIdSort.value,
                  _data.value.filter(item => item.id !== row.id)
                );
              }}
            >
              移除
            </BaseButton>
            <BaseToolTip
              link
              type="primary2"
              content="拖动排序"
              disabled={props.disabled}
            >
              <BaseButton
                style={props.disabled ? '' : 'cursor:grab'}
                disabled={props.disabled}
                link
                class={props.disabled ? '' : DragButtonClassName}
              >
                <BaseIcon color={'var(--xrk-color-primary2)'}>
                  <Sort />
                </BaseIcon>
              </BaseButton>
            </BaseToolTip>
          </div>
        );
      }
    }
  ])
};
const initSortable = () => {
  const getDisabledDragParent = (el: HTMLElement) => {
    if (el.classList.contains(DragDisabledClassName)) {
      return el;
    }
    if (el.parentElement) {
      return getDisabledDragParent(el.parentElement);
    }
    return null;
  };
  const el = TableDomRef.value?.querySelector(
    '.xrk-table__body-wrapper tbody'
  ) as HTMLElement;
  if (el) {
    Sortable.create(el, {
      animation: 150,
      scroll: TableDomRef.value?.querySelector(
        '.xrk-scrollbar__wrap'
      ) as HTMLElement,
      scrollSensitivity: 30,
      handle: `.${DragButtonClassName}`,
      filter: `.${DragDisabledClassName}`,
      onEnd: ({ oldIndex, newIndex }) => {
        _dataIdSort.value.splice(
          newIndex as number,
          0,
          _dataIdSort.value.splice(oldIndex as number, 1)[0]
        );
        _data.value = getSortData(_dataIdSort.value, _data.value);
      },
      onMove: (_, originalEvent) => {
        const targetDOM = originalEvent.target as HTMLElement;
        return !getDisabledDragParent(targetDOM);
      }
    });
  }
};

watch(
  () => _data.value,
  newV => {
    emits('change', newV);
  },
  { deep: true }
);

defineExpose({
  pushData: (data: typeof _data.value) => {
    _data.value.push(...cloneDeep(data));
    setDataSort();
    nextTick(initSortable);
  },
  getAllData: () => getSortData(_dataIdSort.value, _data.value)
});
</script>

<style></style>
